import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { 
  FaArrowLeft,
  FaUser,
  FaBell,
  FaShieldAlt,
  FaGlobe,
  FaDownload,
  FaTrash,
  FaSave,

  FaEye,
  FaEyeSlash,
  FaToggleOn,
  FaToggleOff,
  FaCog,
  FaKey
} from 'react-icons/fa';

interface UserSettings {
  // Información personal
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  language: 'es' | 'en';
  
  // Notificaciones
  notifications: {
    email: boolean;
    push: boolean;
    sms: boolean;
    newJobs: boolean;
    jobUpdates: boolean;
    payments: boolean;
    messages: boolean;
    reminders: boolean;
  };
  
  // Privacidad
  privacy: {
    profileVisibility: 'public' | 'private' | 'businesses';
    showLocation: boolean;
    showEarnings: boolean;
    showRating: boolean;
    allowContact: boolean;
    dataSharing: boolean;
  };
  
  // Preferencias de trabajo
  workPreferences: {
    maxDistance: number;
    minHourlyRate: number;
    preferredSectors: string[];
    availability: {
      monday: { start: string; end: string; available: boolean };
      tuesday: { start: string; end: string; available: boolean };
      wednesday: { start: string; end: string; available: boolean };
      thursday: { start: string; end: string; available: boolean };
      friday: { start: string; end: string; available: boolean };
      saturday: { start: string; end: string; available: boolean };
      sunday: { start: string; end: string; available: boolean };
    };
    autoAccept: boolean;
    autoDecline: boolean;
  };
  
  // Configuración de la aplicación
  appSettings: {
    theme: 'light' | 'dark' | 'auto';
    soundEnabled: boolean;
    vibrationEnabled: boolean;
    autoLocation: boolean;
    dataUsage: 'low' | 'medium' | 'high';
  };
  
  // Seguridad
  security: {
    twoFactorEnabled: boolean;
    biometricEnabled: boolean;
    sessionTimeout: number;
    loginNotifications: boolean;
  };
}

const WorkerSettings: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const db = getFirestore();
  
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Estados para edición
  const [editData, setEditData] = useState<Partial<UserSettings>>({});

  useEffect(() => {
    fetchSettings();
  }, [currentUser]);

  const fetchSettings = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Crear objeto de configuración con valores por defecto
        const defaultSettings: UserSettings = {
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          email: userData.email || '',
          phone: userData.phone || '',
          language: userData.language || 'es',
          
          notifications: {
            email: true,
            push: true,
            sms: false,
            newJobs: true,
            jobUpdates: true,
            payments: true,
            messages: true,
            reminders: true,
            ...userData.notifications
          },
          
          privacy: {
            profileVisibility: 'public',
            showLocation: true,
            showEarnings: false,
            showRating: true,
            allowContact: true,
            dataSharing: false,
            ...userData.privacy
          },
          
          workPreferences: {
            maxDistance: 50,
            minHourlyRate: 15,
            preferredSectors: userData.sector ? [userData.sector] : [],
            availability: {
              monday: { start: '09:00', end: '17:00', available: true },
              tuesday: { start: '09:00', end: '17:00', available: true },
              wednesday: { start: '09:00', end: '17:00', available: true },
              thursday: { start: '09:00', end: '17:00', available: true },
              friday: { start: '09:00', end: '17:00', available: true },
              saturday: { start: '09:00', end: '17:00', available: false },
              sunday: { start: '09:00', end: '17:00', available: false }
            },
            autoAccept: false,
            autoDecline: false,
            ...userData.workPreferences
          },
          
          appSettings: {
            theme: 'light',
            soundEnabled: true,
            vibrationEnabled: true,
            autoLocation: true,
            dataUsage: 'medium',
            ...userData.appSettings
          },
          
          security: {
            twoFactorEnabled: false,
            biometricEnabled: false,
            sessionTimeout: 30,
            loginNotifications: true,
            ...userData.security
          }
        };
        
        setSettings(defaultSettings);
        setEditData(defaultSettings);
      }
    } catch (err) {
      setError(t('settings.error.loading'));
      console.error('Error loading settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!currentUser || !settings) return;
    
    try {
      setSaving(true);
      setError('');
      
      await updateDoc(doc(db, 'users', currentUser.uid), {
        ...editData,
        updatedAt: new Date().toISOString()
      });
      
      setSettings(prev => prev ? { ...prev, ...editData } : null);
      setSuccess(t('settings.success.updated'));
      
      // Cambiar idioma si se modificó
      if (editData.language && editData.language !== i18n.language) {
        i18n.changeLanguage(editData.language);
      }
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(t('settings.error.updating'));
      console.error('Error updating settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!currentUser) return;
    
    if (newPassword !== confirmPassword) {
      setError(t('settings.error.passwordMismatch'));
      return;
    }
    
    if (newPassword.length < 6) {
      setError(t('settings.error.passwordTooShort'));
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      
      // Aquí se implementaría el cambio de contraseña con Firebase Auth
      // await updatePassword(currentUser, newPassword);
      
      setNewPassword('');
      setConfirmPassword('');
      setSuccess(t('settings.success.passwordChanged'));
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(t('settings.error.passwordChange'));
      console.error('Error changing password:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDataExport = async () => {
    if (!currentUser) return;
    
    try {
      // Aquí se implementaría la exportación de datos
      const data = {
        user: settings,
        exportDate: new Date().toISOString()
      };
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `worker-data-${currentUser.uid}.json`;
      a.click();
      URL.revokeObjectURL(url);
      
      setSuccess(t('settings.success.dataExported'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(t('settings.error.dataExport'));
      console.error('Error exporting data:', err);
    }
  };

  const handleAccountDeletion = async () => {
    if (!currentUser || !window.confirm(t('settings.confirm.accountDeletion'))) return;
    
    try {
      setSaving(true);
      setError('');
      
      // Aquí se implementaría la eliminación de la cuenta
      // await deleteUser(currentUser);
      
      setSuccess(t('settings.success.accountDeleted'));
      setTimeout(() => {
        navigate('/auth/login');
      }, 2000);
    } catch (err) {
      setError(t('settings.error.accountDeletion'));
      console.error('Error deleting account:', err);
    } finally {
      setSaving(false);
    }
  };

  const toggleNotification = (key: keyof UserSettings['notifications']) => {
    setEditData(prev => ({
      ...prev,
      notifications: {
        email: prev.notifications?.email ?? settings?.notifications?.email ?? false,
        push: prev.notifications?.push ?? settings?.notifications?.push ?? false,
        sms: prev.notifications?.sms ?? settings?.notifications?.sms ?? false,
        newJobs: prev.notifications?.newJobs ?? settings?.notifications?.newJobs ?? false,
        jobUpdates: prev.notifications?.jobUpdates ?? settings?.notifications?.jobUpdates ?? false,
        payments: prev.notifications?.payments ?? settings?.notifications?.payments ?? false,
        messages: prev.notifications?.messages ?? settings?.notifications?.messages ?? false,
        reminders: prev.notifications?.reminders ?? settings?.notifications?.reminders ?? false,
        [key]: !(prev.notifications?.[key] ?? settings?.notifications?.[key] ?? false)
      }
    }));
  };

  const togglePrivacy = (key: keyof UserSettings['privacy']) => {
    setEditData(prev => ({
      ...prev,
      privacy: {
        profileVisibility: prev.privacy?.profileVisibility ?? settings?.privacy?.profileVisibility ?? 'public',
        showLocation: prev.privacy?.showLocation ?? settings?.privacy?.showLocation ?? false,
        showEarnings: prev.privacy?.showEarnings ?? settings?.privacy?.showEarnings ?? false,
        showRating: prev.privacy?.showRating ?? settings?.privacy?.showRating ?? false,
        allowContact: prev.privacy?.allowContact ?? settings?.privacy?.allowContact ?? false,
        dataSharing: prev.privacy?.dataSharing ?? settings?.privacy?.dataSharing ?? false,
        [key]: !(prev.privacy?.[key] ?? settings?.privacy?.[key] ?? false)
      }
    }));
  };

  const toggleSecurity = (key: keyof UserSettings['security']) => {
    setEditData(prev => ({
      ...prev,
      security: {
        twoFactorEnabled: prev.security?.twoFactorEnabled ?? settings?.security?.twoFactorEnabled ?? false,
        biometricEnabled: prev.security?.biometricEnabled ?? settings?.security?.biometricEnabled ?? false,
        sessionTimeout: prev.security?.sessionTimeout ?? settings?.security?.sessionTimeout ?? 30,
        loginNotifications: prev.security?.loginNotifications ?? settings?.security?.loginNotifications ?? false,
        [key]: !(prev.security?.[key] ?? settings?.security?.[key] ?? false)
      }
    }));
  };

  const toggleAppSetting = (key: keyof UserSettings['appSettings']) => {
    setEditData(prev => ({
      ...prev,
      appSettings: {
        theme: prev.appSettings?.theme ?? settings?.appSettings?.theme ?? 'auto',
        soundEnabled: prev.appSettings?.soundEnabled ?? settings?.appSettings?.soundEnabled ?? false,
        vibrationEnabled: prev.appSettings?.vibrationEnabled ?? settings?.appSettings?.vibrationEnabled ?? false,
        autoLocation: prev.appSettings?.autoLocation ?? settings?.appSettings?.autoLocation ?? false,
        dataUsage: prev.appSettings?.dataUsage ?? settings?.appSettings?.dataUsage ?? 'medium',
        [key]: !(prev.appSettings?.[key] ?? settings?.appSettings?.[key] ?? false)
      }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">{t('settings.error.notFound')}</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'profile', label: t('settings.tabs.profile'), icon: FaUser },
    { id: 'notifications', label: t('settings.tabs.notifications'), icon: FaBell },
    { id: 'privacy', label: t('settings.tabs.privacy'), icon: FaShieldAlt },
    { id: 'work', label: t('settings.tabs.work'), icon: FaCog },
    { id: 'app', label: t('settings.tabs.app'), icon: FaGlobe },
    { id: 'security', label: t('settings.tabs.security'), icon: FaKey }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/worker-dashboard')}
            className="flex items-center gap-2 text-red-600 hover:text-red-700 transition-colors"
          >
            <FaArrowLeft />
            <span>{t('common.back')}</span>
          </button>
          
          <h1 className="text-3xl font-bold text-gray-800">{t('settings.title')}</h1>
          
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            <FaSave />
            <span>{saving ? t('common.saving') : t('common.save')}</span>
          </button>
        </div>

        {/* Mensajes de estado */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
            {success}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar con pestañas */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl p-4">
              <nav className="space-y-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                        activeTab === tab.id
                          ? 'bg-red-100 text-red-700 border border-red-200'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="text-lg" />
                      <span className="font-medium">{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Contenido principal */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-xl p-6">
              {/* Perfil */}
              {activeTab === 'profile' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('settings.profile.title')}</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.profile.firstName')}
                      </label>
                      <input
                        type="text"
                        value={editData.firstName || ''}
                        onChange={(e) => setEditData(prev => ({ ...prev, firstName: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.profile.lastName')}
                      </label>
                      <input
                        type="text"
                        value={editData.lastName || ''}
                        onChange={(e) => setEditData(prev => ({ ...prev, lastName: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.profile.email')}
                      </label>
                      <input
                        type="email"
                        value={editData.email || ''}
                        onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.profile.phone')}
                      </label>
                      <input
                        type="tel"
                        value={editData.phone || ''}
                        onChange={(e) => setEditData(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.profile.language')}
                      </label>
                      <select
                        value={editData.language || 'es'}
                        onChange={(e) => setEditData(prev => ({ ...prev, language: e.target.value as 'es' | 'en' }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      >
                        <option value="es">Español</option>
                        <option value="en">English</option>
                      </select>
                    </div>
                  </div>

                  {/* Cambio de contraseña */}
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('settings.profile.changePassword')}</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('settings.profile.newPassword')}
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('settings.profile.confirmPassword')}
                        </label>
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <button
                      onClick={handlePasswordChange}
                      disabled={!newPassword || !confirmPassword || saving}
                      className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {t('settings.profile.updatePassword')}
                    </button>
                  </div>
                </div>
              )}

              {/* Notificaciones */}
              {activeTab === 'notifications' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('settings.notifications.title')}</h2>
                  
                  <div className="space-y-4">
                    {Object.entries(editData.notifications || {}).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div>
                          <h3 className="font-medium text-gray-800">{t(`settings.notifications.${key}`)}</h3>
                          <p className="text-sm text-gray-600">{t(`settings.notifications.${key}Description`)}</p>
                        </div>
                        <button
                          onClick={() => toggleNotification(key as keyof UserSettings['notifications'])}
                          className={`text-2xl ${value ? 'text-green-500' : 'text-gray-400'}`}
                        >
                          {value ? <FaToggleOn /> : <FaToggleOff />}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Privacidad */}
              {activeTab === 'privacy' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('settings.privacy.title')}</h2>
                  
                  <div className="space-y-4">
                    {Object.entries(editData.privacy || {}).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div>
                          <h3 className="font-medium text-gray-800">{t(`settings.privacy.${key}`)}</h3>
                          <p className="text-sm text-gray-600">{t(`settings.privacy.${key}Description`)}</p>
                        </div>
                        {typeof value === 'boolean' ? (
                          <button
                            onClick={() => togglePrivacy(key as keyof UserSettings['privacy'])}
                            className={`text-2xl ${value ? 'text-green-500' : 'text-gray-400'}`}
                          >
                            {value ? <FaToggleOn /> : <FaToggleOff />}
                          </button>
                        ) : (
                          <select
                            value={value as string}
                            onChange={(e) => setEditData(prev => ({
                              ...prev,
                              privacy: {
                                profileVisibility: prev.privacy?.profileVisibility ?? settings?.privacy?.profileVisibility ?? 'public',
                                showLocation: prev.privacy?.showLocation ?? settings?.privacy?.showLocation ?? false,
                                showEarnings: prev.privacy?.showEarnings ?? settings?.privacy?.showEarnings ?? false,
                                showRating: prev.privacy?.showRating ?? settings?.privacy?.showRating ?? false,
                                allowContact: prev.privacy?.allowContact ?? settings?.privacy?.allowContact ?? false,
                                dataSharing: prev.privacy?.dataSharing ?? settings?.privacy?.dataSharing ?? false,
                                [key]: e.target.value
                              }
                            }))}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          >
                            <option value="public">{t('settings.privacy.visibility.public')}</option>
                            <option value="private">{t('settings.privacy.visibility.private')}</option>
                            <option value="businesses">{t('settings.privacy.visibility.businesses')}</option>
                          </select>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Preferencias de trabajo */}
              {activeTab === 'work' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('settings.work.title')}</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.work.maxDistance')} (km)
                      </label>
                      <input
                        type="number"
                        value={editData.workPreferences?.maxDistance || 50}
                        onChange={(e) => setEditData(prev => ({
                          ...prev,
                          workPreferences: { 
                            maxDistance: parseInt(e.target.value),
                            minHourlyRate: prev.workPreferences?.minHourlyRate ?? settings?.workPreferences?.minHourlyRate ?? 15,
                            preferredSectors: prev.workPreferences?.preferredSectors ?? settings?.workPreferences?.preferredSectors ?? [],
                            availability: prev.workPreferences?.availability ?? settings?.workPreferences?.availability ?? {
                              monday: { start: '09:00', end: '17:00', available: true },
                              tuesday: { start: '09:00', end: '17:00', available: true },
                              wednesday: { start: '09:00', end: '17:00', available: true },
                              thursday: { start: '09:00', end: '17:00', available: true },
                              friday: { start: '09:00', end: '17:00', available: true },
                              saturday: { start: '09:00', end: '17:00', available: false },
                              sunday: { start: '09:00', end: '17:00', available: false }
                            },
                            autoAccept: prev.workPreferences?.autoAccept ?? settings?.workPreferences?.autoAccept ?? false,
                            autoDecline: prev.workPreferences?.autoDecline ?? settings?.workPreferences?.autoDecline ?? false
                          }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('settings.work.minHourlyRate')} ($/hora)
                      </label>
                      <input
                        type="number"
                        value={editData.workPreferences?.minHourlyRate || 15}
                        onChange={(e) => setEditData(prev => ({
                          ...prev,
                          workPreferences: { 
                            maxDistance: prev.workPreferences?.maxDistance ?? settings?.workPreferences?.maxDistance ?? 50,
                            minHourlyRate: parseInt(e.target.value),
                            preferredSectors: prev.workPreferences?.preferredSectors ?? settings?.workPreferences?.preferredSectors ?? [],
                            availability: prev.workPreferences?.availability ?? settings?.workPreferences?.availability ?? {
                              monday: { start: '09:00', end: '17:00', available: true },
                              tuesday: { start: '09:00', end: '17:00', available: true },
                              wednesday: { start: '09:00', end: '17:00', available: true },
                              thursday: { start: '09:00', end: '17:00', available: true },
                              friday: { start: '09:00', end: '17:00', available: true },
                              saturday: { start: '09:00', end: '17:00', available: false },
                              sunday: { start: '09:00', end: '17:00', available: false }
                            },
                            autoAccept: prev.workPreferences?.autoAccept ?? settings?.workPreferences?.autoAccept ?? false,
                            autoDecline: prev.workPreferences?.autoDecline ?? settings?.workPreferences?.autoDecline ?? false
                          }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Disponibilidad semanal */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('settings.work.availability')}</h3>
                    <div className="space-y-3">
                      {Object.entries(editData.workPreferences?.availability || {}).map(([day, schedule]) => (
                        <div key={day} className="flex items-center gap-4 p-3 border border-gray-200 rounded-lg">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={schedule.available}
                              onChange={(e) => setEditData(prev => ({
                                ...prev,
                                workPreferences: {
                                  maxDistance: prev.workPreferences?.maxDistance ?? settings?.workPreferences?.maxDistance ?? 50,
                                  minHourlyRate: prev.workPreferences?.minHourlyRate ?? settings?.workPreferences?.minHourlyRate ?? 15,
                                  preferredSectors: prev.workPreferences?.preferredSectors ?? settings?.workPreferences?.preferredSectors ?? [],
                                  availability: {
                                    monday: prev.workPreferences?.availability?.monday ?? settings?.workPreferences?.availability?.monday ?? { start: '09:00', end: '17:00', available: true },
                                    tuesday: prev.workPreferences?.availability?.tuesday ?? settings?.workPreferences?.availability?.tuesday ?? { start: '09:00', end: '17:00', available: true },
                                    wednesday: prev.workPreferences?.availability?.wednesday ?? settings?.workPreferences?.availability?.wednesday ?? { start: '09:00', end: '17:00', available: true },
                                    thursday: prev.workPreferences?.availability?.thursday ?? settings?.workPreferences?.availability?.thursday ?? { start: '09:00', end: '17:00', available: true },
                                    friday: prev.workPreferences?.availability?.friday ?? settings?.workPreferences?.availability?.friday ?? { start: '09:00', end: '17:00', available: true },
                                    saturday: prev.workPreferences?.availability?.saturday ?? settings?.workPreferences?.availability?.saturday ?? { start: '09:00', end: '17:00', available: false },
                                    sunday: prev.workPreferences?.availability?.sunday ?? settings?.workPreferences?.availability?.sunday ?? { start: '09:00', end: '17:00', available: false },
                                    [day]: { ...schedule, available: e.target.checked }
                                  },
                                  autoAccept: prev.workPreferences?.autoAccept ?? settings?.workPreferences?.autoAccept ?? false,
                                  autoDecline: prev.workPreferences?.autoDecline ?? settings?.workPreferences?.autoDecline ?? false
                                }
                              }))}
                              className="rounded"
                            />
                            <span className="font-medium">{t(`settings.work.days.${day}`)}</span>
                          </div>
                          
                          {schedule.available && (
                            <div className="flex items-center gap-2">
                              <input
                                type="time"
                                value={schedule.start}
                                onChange={(e) => setEditData(prev => ({
                                  ...prev,
                                  workPreferences: {
                                    maxDistance: prev.workPreferences?.maxDistance ?? settings?.workPreferences?.maxDistance ?? 50,
                                    minHourlyRate: prev.workPreferences?.minHourlyRate ?? settings?.workPreferences?.minHourlyRate ?? 15,
                                    preferredSectors: prev.workPreferences?.preferredSectors ?? settings?.workPreferences?.preferredSectors ?? [],
                                    availability: {
                                      monday: prev.workPreferences?.availability?.monday ?? settings?.workPreferences?.availability?.monday ?? { start: '09:00', end: '17:00', available: true },
                                      tuesday: prev.workPreferences?.availability?.tuesday ?? settings?.workPreferences?.availability?.tuesday ?? { start: '09:00', end: '17:00', available: true },
                                      wednesday: prev.workPreferences?.availability?.wednesday ?? settings?.workPreferences?.availability?.wednesday ?? { start: '09:00', end: '17:00', available: true },
                                      thursday: prev.workPreferences?.availability?.thursday ?? settings?.workPreferences?.availability?.thursday ?? { start: '09:00', end: '17:00', available: true },
                                      friday: prev.workPreferences?.availability?.friday ?? settings?.workPreferences?.availability?.friday ?? { start: '09:00', end: '17:00', available: true },
                                      saturday: prev.workPreferences?.availability?.saturday ?? settings?.workPreferences?.availability?.saturday ?? { start: '09:00', end: '17:00', available: false },
                                      sunday: prev.workPreferences?.availability?.sunday ?? settings?.workPreferences?.availability?.sunday ?? { start: '09:00', end: '17:00', available: false },
                                      [day]: { ...schedule, start: e.target.value }
                                    },
                                    autoAccept: prev.workPreferences?.autoAccept ?? settings?.workPreferences?.autoAccept ?? false,
                                    autoDecline: prev.workPreferences?.autoDecline ?? settings?.workPreferences?.autoDecline ?? false
                                  }
                                }))}
                                className="px-2 py-1 border border-gray-300 rounded"
                              />
                              <span>-</span>
                              <input
                                type="time"
                                value={schedule.end}
                                onChange={(e) => setEditData(prev => ({
                                  ...prev,
                                  workPreferences: {
                                    maxDistance: prev.workPreferences?.maxDistance ?? settings?.workPreferences?.maxDistance ?? 50,
                                    minHourlyRate: prev.workPreferences?.minHourlyRate ?? settings?.workPreferences?.minHourlyRate ?? 15,
                                    preferredSectors: prev.workPreferences?.preferredSectors ?? settings?.workPreferences?.preferredSectors ?? [],
                                    availability: {
                                      monday: prev.workPreferences?.availability?.monday ?? settings?.workPreferences?.availability?.monday ?? { start: '09:00', end: '17:00', available: true },
                                      tuesday: prev.workPreferences?.availability?.tuesday ?? settings?.workPreferences?.availability?.tuesday ?? { start: '09:00', end: '17:00', available: true },
                                      wednesday: prev.workPreferences?.availability?.wednesday ?? settings?.workPreferences?.availability?.wednesday ?? { start: '09:00', end: '17:00', available: true },
                                      thursday: prev.workPreferences?.availability?.thursday ?? settings?.workPreferences?.availability?.thursday ?? { start: '09:00', end: '17:00', available: true },
                                      friday: prev.workPreferences?.availability?.friday ?? settings?.workPreferences?.availability?.friday ?? { start: '09:00', end: '17:00', available: true },
                                      saturday: prev.workPreferences?.availability?.saturday ?? settings?.workPreferences?.availability?.saturday ?? { start: '09:00', end: '17:00', available: false },
                                      sunday: prev.workPreferences?.availability?.sunday ?? settings?.workPreferences?.availability?.sunday ?? { start: '09:00', end: '17:00', available: false },
                                      [day]: { ...schedule, end: e.target.value }
                                    },
                                    autoAccept: prev.workPreferences?.autoAccept ?? settings?.workPreferences?.autoAccept ?? false,
                                    autoDecline: prev.workPreferences?.autoDecline ?? settings?.workPreferences?.autoDecline ?? false
                                  }
                                }))}
                                className="px-2 py-1 border border-gray-300 rounded"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Configuración de la aplicación */}
              {activeTab === 'app' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('settings.app.title')}</h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div>
                        <h3 className="font-medium text-gray-800">{t('settings.app.theme')}</h3>
                        <p className="text-sm text-gray-600">{t('settings.app.themeDescription')}</p>
                      </div>
                      <select
                        value={editData.appSettings?.theme || 'light'}
                        onChange={(e) => setEditData(prev => ({
                          ...prev,
                          appSettings: { 
                            theme: e.target.value as 'light' | 'dark' | 'auto',
                            soundEnabled: prev.appSettings?.soundEnabled ?? settings?.appSettings?.soundEnabled ?? false,
                            vibrationEnabled: prev.appSettings?.vibrationEnabled ?? settings?.appSettings?.vibrationEnabled ?? false,
                            autoLocation: prev.appSettings?.autoLocation ?? settings?.appSettings?.autoLocation ?? false,
                            dataUsage: prev.appSettings?.dataUsage ?? settings?.appSettings?.dataUsage ?? 'medium'
                          }
                        }))}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      >
                        <option value="light">{t('settings.app.themes.light')}</option>
                        <option value="dark">{t('settings.app.themes.dark')}</option>
                        <option value="auto">{t('settings.app.themes.auto')}</option>
                      </select>
                    </div>

                    {Object.entries(editData.appSettings || {}).filter(([key]) => key !== 'theme').map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div>
                          <h3 className="font-medium text-gray-800">{t(`settings.app.${key}`)}</h3>
                          <p className="text-sm text-gray-600">{t(`settings.app.${key}Description`)}</p>
                        </div>
                        {typeof value === 'boolean' ? (
                          <button
                            onClick={() => toggleAppSetting(key as keyof UserSettings['appSettings'])}
                            className={`text-2xl ${value ? 'text-green-500' : 'text-gray-400'}`}
                          >
                            {value ? <FaToggleOn /> : <FaToggleOff />}
                          </button>
                        ) : (
                          <select
                            value={value as string}
                            onChange={(e) => setEditData(prev => ({
                              ...prev,
                              appSettings: { 
                                theme: prev.appSettings?.theme ?? settings?.appSettings?.theme ?? 'auto',
                                soundEnabled: prev.appSettings?.soundEnabled ?? settings?.appSettings?.soundEnabled ?? false,
                                vibrationEnabled: prev.appSettings?.vibrationEnabled ?? settings?.appSettings?.vibrationEnabled ?? false,
                                autoLocation: prev.appSettings?.autoLocation ?? settings?.appSettings?.autoLocation ?? false,
                                dataUsage: e.target.value as 'low' | 'medium' | 'high'
                              }
                            }))}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          >
                            <option value="low">{t('settings.app.dataUsage.low')}</option>
                            <option value="medium">{t('settings.app.dataUsage.medium')}</option>
                            <option value="high">{t('settings.app.dataUsage.high')}</option>
                          </select>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Seguridad */}
              {activeTab === 'security' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('settings.security.title')}</h2>
                  
                  <div className="space-y-4">
                    {Object.entries(editData.security || {}).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div>
                          <h3 className="font-medium text-gray-800">{t(`settings.security.${key}`)}</h3>
                          <p className="text-sm text-gray-600">{t(`settings.security.${key}Description`)}</p>
                        </div>
                        {typeof value === 'boolean' ? (
                          <button
                            onClick={() => toggleSecurity(key as keyof UserSettings['security'])}
                            className={`text-2xl ${value ? 'text-green-500' : 'text-gray-400'}`}
                          >
                            {value ? <FaToggleOn /> : <FaToggleOff />}
                          </button>
                        ) : (
                          <select
                            value={value as number}
                            onChange={(e) => setEditData(prev => ({
                              ...prev,
                              security: { 
                                twoFactorEnabled: prev.security?.twoFactorEnabled ?? settings?.security?.twoFactorEnabled ?? false,
                                biometricEnabled: prev.security?.biometricEnabled ?? settings?.security?.biometricEnabled ?? false,
                                sessionTimeout: parseInt(e.target.value),
                                loginNotifications: prev.security?.loginNotifications ?? settings?.security?.loginNotifications ?? false
                              }
                            }))}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          >
                            <option value={15}>15 {t('settings.security.minutes')}</option>
                            <option value={30}>30 {t('settings.security.minutes')}</option>
                            <option value={60}>1 {t('settings.security.hour')}</option>
                            <option value={120}>2 {t('settings.security.hours')}</option>
                          </select>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Acciones de cuenta */}
                  <div className="border-t pt-6 space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800">{t('settings.security.accountActions')}</h3>
                    
                    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div>
                        <h4 className="font-medium text-gray-800">{t('settings.security.exportData')}</h4>
                        <p className="text-sm text-gray-600">{t('settings.security.exportDataDescription')}</p>
                      </div>
                      <button
                        onClick={handleDataExport}
                        className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <FaDownload />
                        <span>{t('settings.security.export')}</span>
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 border border-red-200 rounded-lg bg-red-50">
                      <div>
                        <h4 className="font-medium text-red-800">{t('settings.security.deleteAccount')}</h4>
                        <p className="text-sm text-red-600">{t('settings.security.deleteAccountDescription')}</p>
                      </div>
                      <button
                        onClick={handleAccountDeletion}
                        disabled={saving}
                        className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                      >
                        <FaTrash />
                        <span>{t('settings.security.delete')}</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkerSettings; 